<template>
  <b-form ref="scaleForm" @submit="onSubmit" @reset="onReset" class="needs-validation">
    <b-form-row>
      <b-col lg="4">
        <b-form-group id="questionFormatGroup">
          <span class="fieldHeader">{{$t('privatePage.questionFormat_header')}}:</span>

          <b-form-select id="questionFormatForm"
                        v-model="currentScale.questionFormatId"
                        :options="questionFormats"
                        value-field="id"
                        text-field="name"
                        v-if="!hasContent"
                        novalidate
                        required>
          </b-form-select>
          <span v-else>
            {{currentScale.questionFormat}}
          </span>
        </b-form-group>

        <b-form-group id="credits">
          <span class="fieldHeader">{{$t('privatePage.creditCost_header')}}:</span>
          <b-form-input id="creditsInput"
                        type="number"
                        v-model="currentScale.creditCost"
                        required>
          </b-form-input>
          <template slot="invalid-feedback">
            {{feedbackCredits}}
          </template>
        </b-form-group>

        <b-form-group id="timePerQuestionGroup" style="margin-top:20px; padding-left: 0px;">
          <span class="fieldHeader">{{ $t('privatePage.question_time') }}</span>
          <p style="font-style: italic; margin-bottom: 0px; font-size: 12px;">{{ $t('privatePage.question_time_description') }}</p>
          <b-form-input id="timePerQuestionForm"
                        type="number"
                        required
                        novalidate
                        v-model="currentScale.estimatedQuestionTime">
          </b-form-input>
        </b-form-group>

        <b-form-group id="randomizedTestGroup"
                      v-if="currentScale.questionFormatId === 4">
          <b-form-checkbox id="randomizedTestCheckbox"
                          v-model="currentScale.isRandomLogicTest">
          {{ $t('privatePage.scaleIsRandomizedLogicTest') }}
          </b-form-checkbox>
        </b-form-group>

        <b-form-group id="languageFrom" style="margin-top:20px">
          <span class="fieldHeader">{{$t('lang')}}:</span>
          <EasyDataTable
            :headers="tableFields"
            :items="languages"
            :alternating="true"
            :rows-per-page="10000"
            :hide-rows-per-page="true"
          >
            <template #item-countryCode="item">
              <span :class="'me-1 flagItem flag-icon flag-icon-' + item.countryCode"></span>
              <span> {{item.langName}}</span>
            </template>
            <template #item-name="item">
              <span>{{getContentName(item.langCode)}}</span>
            </template>
            <template #item-buttons="item">
              <div v-if="!languageSelected(item.langCode)">
                <b-button @click.stop="addLanguage(item)"
                          size="sm"
                          class="fa fa-plus me-1"
                          :disabled="isEditingScaleContent">
                </b-button>
              </div>
              <div v-else>
                <b-button @click.stop="editLanguage(item.langCode)"
                          size="sm"
                          class="fa fa-edit me-1"
                          :disabled="isEditingScaleContent">
                </b-button>
                <b-button @click.stop="onDeleteContent(item.langCode)"
                          size="sm"
                          class="fa fa-trash me-1"
                          variant="danger"
                          :disabled="isEditingScaleContent">
                </b-button>
              </div>
            </template>
          </EasyDataTable>
        </b-form-group>

        <b-form-group>
          <b-button type="submit" class="me-1">{{ $t('save') }}</b-button>
          <b-button type="button" class="me-1" @click.stop="onEditQuestions">{{ $t('privatePage.scaleQuestionsHandle') }}</b-button>
          <b-button type="reset" class="me-1" variant="danger">{{ $t('cancel') }}</b-button>
          <b-button @click.stop="onDeleteScale"
                    variant="danger"
                    v-show="!hasContent && currentScale.id">
                    {{ $t('delete') }}
          </b-button>
        </b-form-group>

      </b-col>
    </b-form-row>
  </b-form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'scale-detail-editor',
  computed: {
    ...mapGetters('scalesModule', [
      'isEditingScaleContent',
      'currentScaleContent'
    ]),
    ...mapGetters('testMethodsModule', ['questionFormats']),
    ...mapGetters(['languages']),
    submitMessage() {
      return this.$t('save_ok_feedback')
    },
    feedbackCredits() {
      return this.$t('privatePage.credits_error_feedback')
    },
    deleteMessage() {
      return this.$t('privatePage.scaleDelete_message')
    },
    deleteConfirmationMessage() {
      return this.$t('privatePage.scaleDelete_confirmation')
    },
    deleteTranslationMessage() {
      return this.$t('privatePage.translationDelete_message')
    },
    deleteTranslationConfirmationMessage() {
      return this.$t('privatePage.translationDelete_confirmation')
    },
    tableFields() {
      return [
        { value: 'countryCode', text: '' },
        { value: 'name', text: ' ' },
        { value: 'buttons', text: ' ', tdClass: 'rightAlignedField' }
      ]
    },
    isNew() {
      return !(this.currentScale && this.currentScale.id)
    },
    hasContent() {
      var noOfLanguages = 0
      var hasContent =
        this.currentScale &&
        this.currentScale.contents &&
        this.currentScale.contents.length > 0

      if (!hasContent) return false

      this.currentScale.contents.forEach(content => {
        if (content.languageId) noOfLanguages++
      })
      return noOfLanguages > 0 && hasContent
    }
  },
  methods: {
    ...mapActions('scalesModule', {
      selectCurrentScaleContent: 'selectCurrentScaleContent',
      saveScaleDetails: 'saveScaleDetails',
      setIsEditingScaleContent: 'setIsEditingScaleContent',
      deleteScale: 'deleteScale',
      deleteScaleContent: 'deleteScaleContent'
    }),
    ...mapActions({
      showErrorMessage: 'showErrorMessage',
      showSuccessMessage: 'showSuccessMessage'
    }),
    onSubmit: function(evt) {
      if (this.validateForm()) {
        evt.preventDefault()
        this.saveScaleDetails()
          .then(() => {
            this.setIsEditingScaleContent(false)
            this.showSuccessMessage(this.$t('save_ok_feedback'))
          })
          .catch(error => {
            this.showErrorMessage(error)
          })
      }
    },
    onReset: function(evt) {
      evt.preventDefault()
      this.setIsEditingScaleContent(false)
      this.$router.go(-1)
    },
    validateForm: function() {
      if (
        this.currentScaleContent.languageId !== undefined &&
        this.currentScaleContent.name === ''
      ) {
        this.showErrorMessage(this.$t('name_required_feedback'))
        return false
      }
      if (this.currentScale.questionFormatId === undefined) {
        this.showErrorMessage(
          this.$t('privatePage.scaleQuestionFormatRequired_message')
        )
        return false
      }
      return true
    },
    onDeleteScale: function() {
      if (window.confirm(this.deleteConfirmationMessage)) {
        this.deleteScale()
          .then(() => {
            window.alert(this.deleteMessage)
            this.$router.push('/private/scales')
          })
          .catch(error => {
            this.showErrorMessage(error)
          })
      }
    },
    onEditQuestions: function() {
      this.$router.push({
        name: 'scalequestions',
        params: {
          currentScaleId: this.currentScale.id,
          currentScaleQuestionFormatId: this.currentScale.questionFormatId,
          currentScaleIsRandomLogicTest: this.currentScale.isRandomLogicTest
        }
      })
    },
    addLanguage: function(language) {
      this.selectCurrentScaleContent({ languageId: language.languageId }).then(
        () => {
          this.setIsEditingScaleContent(true).then(() => {
            this.$nextTick().then(() => {
              this.$emit('onEdit')
            })
          })
        }
      )
    },
    onDeleteContent: function(langCode) {
      if (window.confirm(this.deleteTranslationConfirmationMessage)) {
        let content = this.getContent(langCode)
        this.deleteScaleContent(content)
          .then(() => {
            this.setIsEditingScaleContent(false)
            this.showSuccessMessage(this.deleteTranslationMessage)
          })
          .catch(error => {
            this.showErrorMessage(error)
          })
      }
    },
    editLanguage: function(langCode) {
      this.selectCurrentScaleContent(this.getContent(langCode))
      this.setIsEditingScaleContent(true)
      this.$emit('onEdit')
    },
    getContent(langCode) {
      var content = null

      if (!this.hasContent) {
        return null
      }

      for (let i = 0; i < this.currentScale.contents.length; i++) {
        var item = this.currentScale.contents[i]
        if (item.langCode === langCode) {
          content = item
          break
        }
      }

      return content
    },
    languageSelected(langCode) {
      return this.getContent(langCode) !== null
    },
    countDownChanged: function(dismissCountDown) {
      this.alerts.countdown = dismissCountDown
    },
    showMessage: function(message) {
      this.alerts.message = message
      this.alerts.countdown = this.alerts.dismissSecs
      this.alerts.variant = 'info'
    },
    showErrorMessage: function(message) {
      this.alerts.message = message
      this.alerts.countdown = this.alerts.dismissSecs
      this.alerts.variant = 'danger'
    },
    showSuccessMessage: function(message) {
      this.alerts.message = message
      this.alerts.countdown = this.alerts.dismissSecs
      this.alerts.variant = 'success'
    },
    getContentName(langCode) {
      let name = ''

      if (this.hasContent) {
        this.currentScale.contents.forEach(content => {
          if (langCode === content.langCode) {
            name = content.name
          }
        })
      }

      return name
    }
  },
  data() {
    return {
      alerts: {
        dismissSecs: 3,
        countdown: 0,
        message: '',
        variant: 'warning'
      }
    }
  },
  props: {
    currentScale: {}
  }
}
</script>
